import { GIFTING_CATEGORY_CONFIG } from './giftingCategoryMenuConstants';

const flattenMenuCategories = (menuV2) => {
  if (!Array.isArray(menuV2)) return [];

  const flattened = [];
  menuV2.forEach((entry) => {
    if (entry) flattened.push(entry);
    if (Array.isArray(entry?.children)) {
      entry.children.forEach((child) => {
        if (child) flattened.push(child);
      });
    }
  });
  return flattened;
};

const normalizeKey = (value) =>
  (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const categoryMatchesGiftingConfig = (category, config) => {
  if (!category || !config) return false;

  if (config.menuSlugs?.includes(category.slug)) {
    return true;
  }

  const categoryKey = normalizeKey(category.category);
  const labelKey = normalizeKey(config.label);
  if (categoryKey && labelKey && categoryKey === labelKey) {
    return true;
  }

  if (config.id === 'packs') {
    const slugKey = normalizeKey(category.slug);
    return slugKey.includes('pack') || slugKey.includes('box');
  }

  if (config.id === 'cakes') {
    const slugKey = normalizeKey(category.slug);
    const combined = `${slugKey}${categoryKey}`;
    return combined.includes('cake');
  }

  if (config.id === 'ice-cream') {
    const slugKey = normalizeKey(category.slug);
    const combined = `${slugKey}${categoryKey}`;
    return combined.includes('icecream');
  }

  return false;
};

export const findMenuCategoryBySlugs = (menuV2, menuSlugs) => {
  if (!menuSlugs?.length) return null;
  const flat = flattenMenuCategories(menuV2);
  return (
    menuSlugs
      .map((slug) => flat.find((category) => category.slug === slug))
      .find((category) => category && Array.isArray(category.products)) || null
  );
};

/** Tab thumb when sample asset missing; image field order matches Menu primaryImage. */
export const getMenuProductImage = (product) =>
  product?.productImageSmall || product?.productImage || product?.accentImage || '';

/** Product carousel cards; prefer small URL for load time at display size. */
export const getGiftingProductCardImage = (product) =>
  product?.productImageSmall || product?.productImage || product?.accentImage || '';

export const getProductsForCategorySlug = (menuV2, menuSlugs) => {
  const category = findMenuCategoryBySlugs(menuV2, menuSlugs);
  return category?.products || [];
};

/** Merge products for a gifting tab (slug list + title/slug heuristics). */
export const getMergedProductsForGiftingConfig = (menuV2, config) => {
  if (!menuV2 || !config) return [];

  const flat = flattenMenuCategories(menuV2);
  const seen = new Set();
  const merged = [];

  const pushProducts = (category) => {
    (category?.products || []).forEach((product) => {
      const key = product?.id || product?.productSlug;
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(product);
    });
  };

  (config.menuSlugs || []).forEach((slug) => {
    const bySlug = flat.find((category) => category.slug === slug);
    pushProducts(bySlug);
  });

  flat.filter((category) => categoryMatchesGiftingConfig(category, config)).forEach(pushProducts);

  return merged;
};

/** @deprecated Use getMergedProductsForGiftingConfig */
export const getMergedProductsForMenuSlugs = (menuV2, menuSlugs) => {
  const config = GIFTING_CATEGORY_CONFIG.find((entry) =>
    entry.menuSlugs?.some((slug) => menuSlugs?.includes(slug))
  );
  if (config) {
    return getMergedProductsForGiftingConfig(menuV2, config);
  }
  return getProductsForCategorySlug(menuV2, menuSlugs);
};

/**
 * Build tab items from store menu; fill gaps from sample items (images / empty products).
 */
export const buildGiftingCategoryMenuItems = (menuV2, sampleItems) => {
  const samples = Array.isArray(sampleItems) ? sampleItems : [];

  return GIFTING_CATEGORY_CONFIG.map((config) => {
    const sample = samples.find((item) => item.id === config.id) || {};
    const products = menuV2 ? getMergedProductsForGiftingConfig(menuV2, config) : [];
    const menuCategory =
      findMenuCategoryBySlugs(menuV2, config.menuSlugs) ||
      flattenMenuCategories(menuV2).find((category) =>
        categoryMatchesGiftingConfig(category, config)
      );
    const firstProduct = products[0];

    return {
      id: config.id,
      label: config.label,
      menuSlug: menuCategory?.slug || config.menuSlugs[0],
      menuSlugs: config.menuSlugs,
      imageUrl: sample.imageUrl || getMenuProductImage(firstProduct) || '',
      imageAlt: sample.imageAlt || config.label,
      products,
    };
  });
};

/**
 * Same rule as Menu.jsx evaluateQuery: selected store id or sample menu store.
 */
export const resolveGiftingMenuStoreId = (selectedStore, sampleMenuStoreId) => {
  const id = selectedStore?.id;
  if (id !== undefined && id !== null && id !== '') {
    return id;
  }
  return sampleMenuStoreId;
};

export const isGiftingSampleMenu = (selectedStore, sampleMenuStoreId) => {
  const id = selectedStore?.id;
  if (id === undefined || id === null || id === '') {
    return true;
  }
  return parseInt(id, 10) === parseInt(sampleMenuStoreId, 10);
};
